import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { LegacyResponseApplicationError } from '@verantos-dev/types-common';

import { ApiWorkflowDialogSteps } from './enums';
import type { ApiWorkflowDialogMessages } from './types';

export const getSaveButtonLabel = ({
  currentStep,
  initialLoadingError,
  isInitialLoading,
  isSubmitting,
  submittingError,
  onRetryInitialLoadingFetch,
  onRetrySubmittingFetch,
  dialogMessages,
}: {
  currentStep: ApiWorkflowDialogSteps;
  initialLoadingError?: FetchBaseQueryError | SerializedError | undefined;
  isInitialLoading?: boolean;
  isSubmitting?: boolean;
  submittingError?:
    | LegacyResponseApplicationError[]
    | FetchBaseQueryError
    | SerializedError
    | undefined;
  onRetryInitialLoadingFetch?: () => void;
  onRetrySubmittingFetch?: () => void;
  dialogMessages?: ApiWorkflowDialogMessages;
}) => {
  if (currentStep === ApiWorkflowDialogSteps.UserEntry) {
    if (initialLoadingError) {
      const loadingErrorRetryText = dialogMessages?.loadingErrorSaveButtonText || 'Retry';
      const loadingErrorCloseText = dialogMessages?.loadingErrorSaveButtonText || 'Close';
      return onRetryInitialLoadingFetch ? loadingErrorRetryText : loadingErrorCloseText;
    }

    if (isInitialLoading) {
      return dialogMessages?.loadingSaveButtonText || 'Save';
    }

    return dialogMessages?.entrySaveButtonText || 'Save';
  }

  if (currentStep === ApiWorkflowDialogSteps.UserConfirmation) {
    return dialogMessages?.confirmationSaveButtonText || 'Save';
  }

  if (currentStep === ApiWorkflowDialogSteps.UserSubmitted) {
    if (submittingError) {
      const submittingErrorRetryText = dialogMessages?.submittingErrorSaveButtonText || 'Retry';
      const submittingErrorCloseText = dialogMessages?.submittingErrorSaveButtonText || 'Close';
      return onRetrySubmittingFetch ? submittingErrorRetryText : submittingErrorCloseText;
    }

    if (isSubmitting) {
      return dialogMessages?.submittingSaveButtonText || 'Save';
    }

    return dialogMessages?.successCloseButtonText || 'Close';
  }

  return 'Okay';
};

export const getCancelButtonLabel = ({
  currentStep,
  isSubmitting,
  dialogMessages,
  isInitialLoading,
  initialLoadingError,
  submittingError,
}: {
  currentStep: ApiWorkflowDialogSteps;
  isSubmitting?: boolean;
  dialogMessages?: ApiWorkflowDialogMessages;
  isInitialLoading: boolean;
  initialLoadingError: FetchBaseQueryError | SerializedError | undefined;
  submittingError:
    | LegacyResponseApplicationError[]
    | FetchBaseQueryError
    | SerializedError
    | undefined;
}) => {
  if (currentStep === ApiWorkflowDialogSteps.UserEntry) {
    if (initialLoadingError) {
      return dialogMessages?.entryCancelButtonText || 'Cancel';
    }

    if (isInitialLoading) {
      return dialogMessages?.loadingCancelButtonText || 'Cancel';
    }

    return dialogMessages?.entryCancelButtonText || 'Cancel';
  }

  if (currentStep === ApiWorkflowDialogSteps.UserConfirmation) {
    return dialogMessages?.confirmationCancelButtonText || 'Back';
  }

  if (currentStep === ApiWorkflowDialogSteps.UserSubmitted) {
    if (submittingError) {
      return dialogMessages?.submittingErrorCancelButtonText || 'Cancel';
    }

    if (isSubmitting) {
      return dialogMessages?.submittingCancelButtonText || 'Cancel';
    }

    return dialogMessages?.successCloseButtonText || 'Cancel';
  }

  return 'Cancel';
};
